import Loading from '@/components/custom/loading'
import {
    SpEditorProvider,
    useSpEditorProvider,
} from '@/features/screenplay/components/editor/SpEditorProvider'
import SpEditorSidebar from '@/features/screenplay/components/editor/SpEditorSidebar'
import { SpOptionBar } from '@/features/screenplay/components/sp-editor-optionbar'
import SpContent from '@/features/screenplay/components/SpContent'
import { useWorkspace } from '@/features/workspace/stores/workspace.store'
import { getScreenplay } from '@/service/local-db'
import { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { WorkspaceLayout } from '@/features/workspace'

import { ScreenplayOutlineGenerationDialog } from '../components/ScreenplayOutlineGenerationDialog'
import { OverwriteAppendDialog } from '../components/OverwriteAppendDialog'
import { useGenerateScreenplayOutlineMutation } from '../queries/screenplay.ai.queries'
import { useLocation } from 'react-router-dom'
import { toast } from 'sonner'
import { useState } from 'react'
import { useSceneStore } from '@/features/shared/stores/scene.store'
import { setJsonScriptEditor } from '../components/editor/utils/JsonUtils'

import { useStoryQuery } from '@/features/story/queries/story.queries'
import { useSpEditorStore } from '../stores/sp-editor.store'

export const SpEditorProviderWrapper = ({ children }: { children: React.ReactNode }) => (
    <SpEditorProvider>{children}</SpEditorProvider>
)
export default function ScreenplayPage() {
    return (
        <WorkspaceLayout
            ProviderWrapper={SpEditorProviderWrapper}
            SidebarComponent={<SpEditorSidebar />}
            optionBar={<SpOptionBar />}
        >
            <ScreenplayPageContent />
        </WorkspaceLayout>
    )
}

export function ScreenplayPageContent() {
    const { screenplayEntity, setScreenplayEntity } = useSpEditorProvider()
    const isOutlineDialogOpen = useSpEditorStore((state) => state.isOutlineDialogOpen)
    const setIsOutlineDialogOpen = useSpEditorStore((state) => state.setIsOutlineDialogOpen)
    const isChoiceDialogOpen = useSpEditorStore((state) => state.isChoiceDialogOpen)
    const setIsChoiceDialogOpen = useSpEditorStore((state) => state.setIsChoiceDialogOpen)

    const { project } = useWorkspace()
    const { editor } = useSceneStore()
    const fileName = 'master'
    const router = useNavigate()
    const location = useLocation()

    const { data: story } = useStoryQuery(project?.id, 'master')

    const [storyContentForOutline, setStoryContentForOutline] = useState<string>('')
    const [insertionMode, setInsertionMode] = useState<'overwrite' | 'append'>('overwrite')

    useEffect(() => {
        if (!project?.id) return
        async function fetchScreenplay() {
            if (!project) return
            const screenplay = await getScreenplay(project.id, fileName)
            setScreenplayEntity(screenplay)
            if (screenplay == undefined) {
                router(`/workspace/${project.id}/screenplay/add`)
            }
        }

        fetchScreenplay().catch((error) => {
            console.error('Error fetching screenplay:', error)
            setScreenplayEntity(undefined)
        })
    }, [project])

    useEffect(() => {
        const content = location.state?.storyContent
        const contentString = Array.isArray(content)
            ? content.map((c: any) => c.children?.map((ch: any) => ch.text).join('')).join('\n')
            : ''

        if (location.state?.openChoiceDialog) {
            setStoryContentForOutline(contentString)
            setIsChoiceDialogOpen(true)
            router(location.pathname, { replace: true, state: {} })
        } else if (location.state?.openGenerationDialog) {
            setIsOutlineDialogOpen(true)
            setInsertionMode('overwrite')
            setStoryContentForOutline(contentString)
            router(location.pathname, { replace: true, state: {} })
        } else if (story?.content) {
            // Pre-fill story content if available when triggered from screenplay
            const contentString = Array.isArray(story.content) ? story.content.map((c: any) => c.children?.map((ch: any) => ch.text).join('')).join('\n') : ''
            setStoryContentForOutline(contentString)
        }
    }, [location, router, setIsChoiceDialogOpen, setIsOutlineDialogOpen, story])

    const generateOutlineMutation = useGenerateScreenplayOutlineMutation({
        onSuccess: (data) => {
            if (insertionMode === 'overwrite') {
                setJsonScriptEditor(editor, data)
            } else {
                const currentContent = screenplayEntity?.content || []
                setJsonScriptEditor(editor, [...currentContent, ...data])
            }
            toast.success('Screenplay outline generated!')
            setIsOutlineDialogOpen(false)
        },
        onError: (error) => {
            toast.error(`Generation failed: ${error.message}`)
        }
    })

    const handleChoice = (choice: 'overwrite' | 'append') => {
        setInsertionMode(choice)
        setIsOutlineDialogOpen(true)
    }

    if (!screenplayEntity) {
        return <Loading />
    }
    return (
        <>
            <SpContent />
            <OverwriteAppendDialog
                isOpen={isChoiceDialogOpen}
                onOpenChange={setIsChoiceDialogOpen}
                onChoice={handleChoice}
            />
            <ScreenplayOutlineGenerationDialog
                isOpen={isOutlineDialogOpen}
                onOpenChange={setIsOutlineDialogOpen}
                onGenerate={(options) => generateOutlineMutation.mutate(options)}
                storyContent={storyContentForOutline}
                isLoading={generateOutlineMutation.isPending}
            />
        </>
    )
}
